import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { SNFieldValue, SNSmartTeamTicket, SNTicketLocation } from '@nsc/servicenow/types.js';
import { ServiceNowTicket, ServiceNowTicketPart } from '../db/ServiceNowTicket.js';
import { TicketLocationCache } from '../db/TicketLocationCache.js';
import { resolveDellReplyCode } from './_dell-reply-codes.js';
import { parseLocationCoords } from './_sn-coords.js';

/**
 * Mapping helpers for the colleague view. Kept apart from `ticket-queries.ts` so the self-ticket
 * path (which the whole FE workflow depends on) isn't touched by this feature.
 */

/** Geocode a free-text address through Google. Returns null when unresolvable or unconfigured. */
export async function geocode(address: string): Promise<{ lat: number; lng: number } | null> {
  const apiKey = process.env.GOOGLE_API_KEY;
  if (!apiKey) return null;

  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
  const res = await fetch(url).catch(() => null);
  if (!res?.ok) return null;

  const data = (await res.json().catch(() => null)) as {
    status?: string;
    results?: Array<{ geometry?: { location?: { lat: number; lng: number } } }>;
  } | null;
  if (data?.status !== 'OK') return null;

  const location = data.results?.[0]?.geometry?.location;
  return location ? { lat: location.lat, lng: location.lng } : null;
}

/** Flatten an SN location object into a single display line. */
function composeLocationLabel(loc: SNTicketLocation | undefined): string | undefined {
  if (!loc) return undefined;
  const parts = [loc.street, loc.city, loc.state, loc.zip, loc.country]
    .map(p => p?.display_value?.trim())
    .filter((p): p is string => !!p);
  if (parts.length > 0) return parts.join(', ');
  return loc.reference?.display_value?.trim() || undefined;
}

function snBool(field: SNFieldValue | undefined): boolean {
  return field?.value === '1' || field?.display_value?.toLowerCase() === 'true';
}

/**
 * Resolve a ticket's coordinates: SN's own values first, then the shared geocode cache, then
 * Google. Roughly one ticket in eight arrives without coordinates, and a pin missing from the map
 * is exactly the ticket an FE is looking for — so the fallback is worth the call.
 */
async function resolveCoords(
  ticketNumber: string | undefined,
  addressLabel: string | undefined,
): Promise<{ lat: number; lng: number } | null> {
  if (!ticketNumber || !addressLabel) return null;

  const col = await getRawCollection<TicketLocationCache>(TicketLocationCache._collectionName);
  const cached = await col.findOne({ ticketNumber });
  if (cached?.addressLabel === addressLabel && typeof cached.lat === 'number' && typeof cached.lng === 'number') {
    return { lat: cached.lat, lng: cached.lng };
  }

  const geo = await geocode(addressLabel);
  if (!geo) return null;

  await col.updateOne(
    { ticketNumber },
    { $set: { ticketNumber, addressLabel, lat: geo.lat, lng: geo.lng, geocodedAt: new Date() } },
    { upsert: true },
  );
  return geo;
}

/**
 * Map an SN ticket onto the entity the colleague list and map render.
 *
 * Deliberately *not* shared with `mapSNTicket` in ticket-queries.ts: that one feeds the FE's own
 * workflow (pending actions, problem-report toggles, assignee guards) and carries none of the
 * scheduling-window / notes fields this view needs.
 *
 * `replyCode` and `partsStatusLabel` are left null on purpose — see the field docs on
 * {@link ServiceNowTicket}. The UI renders a "TBA" placeholder for both.
 */
export async function mapSNTicketForColleagueView(sn: SNSmartTeamTicket): Promise<ServiceNowTicket> {
  const number = sn.number?.display_value || sn.number?.value || undefined;
  const locationLabel = composeLocationLabel(sn.location);

  let { lat, lng } = parseLocationCoords(sn.location);
  if (lat === undefined || lng === undefined) {
    const geo = await resolveCoords(number, locationLabel);
    if (geo) {
      lat = geo.lat;
      lng = geo.lng;
    }
  }

  const assignedTo = sn.assignment?.assigned_to ?? sn.assigned_to;
  // `value` first: it is the short code (`P05`, `CCL`), which is what the flag icon has room for.
  // `display_value` is the composite `PND - Parts not dispatched [P05]` and is only a fallback —
  // the resolver understands both shapes.
  const replyCode = resolveDellReplyCode(sn.replycode?.value || sn.replycode?.display_value);

  return new ServiceNowTicket({
    number,
    title: sn.short_description?.display_value || undefined,
    description: sn.description?.display_value || undefined,
    assignedToSysId: assignedTo?.value || undefined,
    assignedToName: assignedTo?.display_value || undefined,
    state: sn.state?.value || undefined,
    stateLabel: sn.state?.display_value || undefined,
    substate: sn.substate?.value || undefined,
    substateLabel: sn.substate?.display_value || undefined,
    locationLabel,
    locationLat: lat,
    locationLng: lng,
    pudoLocationLabel: composeLocationLabel(sn.pudo_location),
    pudoLocationLat: parseLocationCoords(sn.pudo_location).lat,
    pudoLocationLng: parseLocationCoords(sn.pudo_location).lng,
    correlationId: sn.serviceContext?.correlation_id?.value || undefined,
    clientSysId: sn.customer?.reference?.value || undefined,
    clientName: sn.customer?.reference?.display_value || undefined,
    clientCode: sn.customer?.code?.display_value || undefined,
    contactName: sn.contact?.reference?.display_value || undefined,
    contactPhone: sn.contact?.phone?.display_value || undefined,
    contactEmail: sn.contact?.email?.display_value || undefined,
    windowStart: sn.scheduling?.window_start?.value || undefined,
    windowEnd: sn.scheduling?.window_end?.value || undefined,
    fulfilmentStatusLabel: sn.fulfilment_status?.display_value || undefined,
    // Resolved rather than passed through: ServiceNow stores both vocabularies in one field, so a
    // raw value would show `S03` on one ticket and `CNL3` on the next for the same situation.
    // No-ops until Cegeka adds `reply_code` to the payload (see SNSmartTeamTicket.reply_code).
    replyCode: replyCode?.label,
    replyCodeMeaning: replyCode?.meaning,
    replyCodeDisplay: sn.replycode?.display_value || undefined,
    expectedStart: sn.expected_start?.value || undefined,
    expectedStartDisplay: sn.expected_start?.display_value || undefined,
    parts: (sn.parts ?? []).map(
      p =>
        new ServiceNowTicketPart({
          description: p.short_description?.display_value || p.model_number?.display_value || undefined,
          modelNumber: p.model_number?.display_value || undefined,
          manufacturer: p.manufacturer?.display_value || undefined,
          quantity: p.required_quantity?.display_value || undefined,
          stage: p.stage?.display_value || undefined,
          sourced: snBool(p.sourced),
          delivered: snBool(p.delivered),
          returnDefective: snBool(p.return_defective),
          assetLocation: p.asset?.location?.display_value || undefined,
          poNumber: p.asset?.po_number?.display_value || undefined,
        }),
    ),
    workNotesHtml: [],
  });
}
